import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { VentasResumenProductoRow } from '../../services/ventasInformesService';
import { labelTipoArticulo } from '../../config/tipoArticuloOptions';
import { aggregateByField, FilaAgregada, normalizarClasificacion, pctDelMonto, pctRentabilidad, sumRows } from '../../utils/agruparVentasResumenProducto';

const TAB_RAMO = 0;
const TAB_LINEA = 1;
const TAB_CLASE = 2;
const TAB_PRODUCTO = 3;

const CHIP_CLASIFICACION_SX = {
  maxWidth: '100%',
  height: 'auto',
  minHeight: 22,
  alignSelf: 'flex-start',
  '& .MuiChip-label': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    overflow: 'visible',
    fontSize: '0.6875rem',
    lineHeight: 1.35,
    px: '6px',
    py: '4px',
    fontWeight: 500,
    display: 'block',
  },
} as const;

const RESUMEN_COL = {
  producto: { width: '14%' },
  tipoArt: { width: '6%' },
  ramo: { width: '7%' },
  linea: { width: '7%' },
  clase: { width: '7%' },
  cantidad: { width: '5%' },
  monto: { width: '9%' },
  pctMonto: { width: '6%' },
  costo: { width: '9%' },
  pctRentab: { width: '6%' },
  peso: { width: '7%' },
  volumen: { width: '7%' },
  accion: { width: '5%' },
} as const;

const currencyFormatter = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  minimumFractionDigits: 2,
});

const qtyFormatter = new Intl.NumberFormat('es-AR', {
  maximumFractionDigits: 0,
});

const decimal3Formatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

const pctFormatter = new Intl.NumberFormat('es-AR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const fmtPctRentab = (monto: number, costo: number): string => {
  const pct = pctRentabilidad(monto, costo);
  return pct != null ? `${pctFormatter.format(pct)}%` : '—';
};

type ResumenClasificacionTabsProps = {
  rows: VentasResumenProductoRow[];
  totalMonto: number;
  totales: {
    totalCantidad: number;
    totalMonto: number;
    totalCosto: number;
    totalPeso: number;
    totalVolumen: number;
  };
  onAbrirDetalle: (row: VentasResumenProductoRow) => void;
};

type NivelChip = 'ramo' | 'linea' | 'clase';

type FiltroDrillDown = {
  nivel: NivelChip;
  nombre: string;
};

const CAMPO_POR_NIVEL: Record<NivelChip, 'nombreRamo' | 'nombreLinea' | 'nombreClase'> = {
  ramo: 'nombreRamo',
  linea: 'nombreLinea',
  clase: 'nombreClase',
};

const LABEL_NIVEL: Record<NivelChip, string> = {
  ramo: 'Ramo',
  linea: 'Línea',
  clase: 'Clase',
};

function filtrarRowsPorDrillDown(rows: VentasResumenProductoRow[], filtro: FiltroDrillDown): VentasResumenProductoRow[] {
  const campo = CAMPO_POR_NIVEL[filtro.nivel];
  return rows.filter((r) => normalizarClasificacion(r[campo]) === filtro.nombre);
}

const CHIP_NIVEL_STYLES: Record<NivelChip, { bgcolor: string; color: string }> = {
  ramo: { bgcolor: '#e3f2fd', color: '#1565c0' },
  linea: { bgcolor: '#e8f5e9', color: '#2e7d32' },
  clase: { bgcolor: '#f3e5f5', color: '#6a1b9a' },
};

const ChipClasificacion: React.FC<{ nivel: NivelChip; nombre: string }> = ({ nivel, nombre }) => {
  const t = (nombre ?? '').trim();
  if (!t) return <>—</>;
  const { bgcolor, color } = CHIP_NIVEL_STYLES[nivel];
  return (
    <Chip
      label={t}
      size="small"
      sx={{
        ...CHIP_CLASIFICACION_SX,
        bgcolor,
        color,
      }}
    />
  );
};

type TablaAgregadaProps = {
  label: string;
  nivel: NivelChip;
  filas: FilaAgregada[];
  totales: ResumenClasificacionTabsProps['totales'];
  onDrillDown: (nivel: NivelChip, nombre: string) => void;
};

const TablaAgregada: React.FC<TablaAgregadaProps> = ({ label, nivel, filas, totales, onDrillDown }) => (
  <TableContainer>
    <Table size="small" sx={{ tableLayout: 'fixed' }}>
      <TableHead>
        <TableRow sx={{ bgcolor: '#fafafa' }}>
          <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{label}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Cant.
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Monto
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            % Monto
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Costo
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            % Rentab.
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Peso (kg)
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.72rem' }}>
            Vol. (m³)
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {filas.map((f) => (
          <TableRow
            key={f.nombre}
            hover
            onClick={() => onDrillDown(nivel, f.nombre)}
            sx={{ cursor: 'pointer' }}
          >
            <TableCell sx={{ py: 1, verticalAlign: 'top', overflow: 'visible' }}>
              <Tooltip title={`Ver productos de este ${label.toLowerCase()}`} arrow>
                <Box component="span" sx={{ display: 'inline-block' }}>
                  <ChipClasificacion nivel={nivel} nombre={f.nombre} />
                </Box>
              </Tooltip>
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {qtyFormatter.format(f.cantidad)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {currencyFormatter.format(f.montoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {pctFormatter.format(f.pctMonto)}%
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {currencyFormatter.format(f.costoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {fmtPctRentab(f.montoTotal, f.costoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {decimal3Formatter.format(f.pesoTotal)}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
              {decimal3Formatter.format(f.volumenTotal)}
            </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700 } }}>
          <TableCell sx={{ fontSize: '0.8rem' }}>TOTAL</TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {qtyFormatter.format(totales.totalCantidad)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {currencyFormatter.format(totales.totalMonto)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
            100%
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {currencyFormatter.format(totales.totalCosto)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {fmtPctRentab(totales.totalMonto, totales.totalCosto)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {decimal3Formatter.format(totales.totalPeso)}
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}>
            {decimal3Formatter.format(totales.totalVolumen)}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
);

type TablaProductosProps = {
  rows: VentasResumenProductoRow[];
  totalMonto: number;
  totales: ResumenClasificacionTabsProps['totales'];
  filtroDrillDown: FiltroDrillDown | null;
  onLimpiarFiltro: () => void;
  onAbrirDetalle: (row: VentasResumenProductoRow) => void;
};

const TablaProductos: React.FC<TablaProductosProps> = ({
  rows,
  totalMonto,
  totales,
  filtroDrillDown,
  onLimpiarFiltro,
  onAbrirDetalle,
}) => {
  const theme = useTheme();

  const filasOrdenadas = useMemo(
    () => [...rows].sort((a, b) => b.montoTotal - a.montoTotal),
    [rows]
  );

  return (
    <Box>
      {filtroDrillDown ? (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label={`${LABEL_NIVEL[filtroDrillDown.nivel]}: ${filtroDrillDown.nombre} · ${filasOrdenadas.length} producto${filasOrdenadas.length === 1 ? '' : 's'}`}
            size="small"
            onDelete={onLimpiarFiltro}
            sx={{
              height: 'auto',
              minHeight: 28,
              maxWidth: '100%',
              fontWeight: 600,
              ...CHIP_NIVEL_STYLES[filtroDrillDown.nivel],
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
                py: 0.75,
                lineHeight: 1.35,
              },
            }}
          />
        </Box>
      ) : null}
      {filasOrdenadas.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3 }}>
          No hay productos para el filtro seleccionado.
        </Typography>
      ) : (
    <TableContainer sx={{ width: '100%', overflowX: 'hidden' }}>
      <Table
        size="small"
        sx={{
          width: '100%',
          tableLayout: 'fixed',
          '& .MuiTableCell-root': { px: 0.75, py: 0.6 },
        }}
      >
        <TableHead>
          <TableRow sx={{ bgcolor: '#fafafa' }}>
            <TableCell sx={{ ...RESUMEN_COL.producto, fontSize: '0.75rem', fontWeight: 700 }}>
              Producto
            </TableCell>
            <TableCell sx={{ ...RESUMEN_COL.tipoArt, fontSize: '0.7rem', fontWeight: 700 }} title="Tipo de artículo">
              Tipo
            </TableCell>
            <TableCell sx={{ ...RESUMEN_COL.ramo, fontSize: '0.75rem', fontWeight: 700 }}>Ramo</TableCell>
            <TableCell sx={{ ...RESUMEN_COL.linea, fontSize: '0.75rem', fontWeight: 700 }}>Línea</TableCell>
            <TableCell sx={{ ...RESUMEN_COL.clase, fontSize: '0.75rem', fontWeight: 700 }}>Clase</TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.cantidad, fontSize: '0.72rem', fontWeight: 700 }}>
              Cant.
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.monto, fontSize: '0.72rem', fontWeight: 700 }}>
              Monto
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.pctMonto, fontSize: '0.72rem', fontWeight: 700 }}>
              % Monto
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.costo, fontSize: '0.72rem', fontWeight: 700 }}>
              Costo
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.pctRentab, fontSize: '0.72rem', fontWeight: 700 }}>
              % Rentab.
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.peso, fontSize: '0.72rem', fontWeight: 700 }}>
              Peso (kg)
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.volumen, fontSize: '0.72rem', fontWeight: 700 }}>
              Vol. (m³)
            </TableCell>
            <TableCell
              align="center"
              className="no-print"
              aria-hidden
              sx={{ ...RESUMEN_COL.accion, width: RESUMEN_COL.accion.width, px: 0.25 }}
            />
          </TableRow>
        </TableHead>
        <TableBody>
          {filasOrdenadas.map((row, idx) => (
            <TableRow
              key={`${row.producto}-${row.tipo}-${row.nombreRamo}-${row.nombreLinea}-${row.nombreClase}-${idx}`}
              sx={{ '&:nth-of-type(odd)': { bgcolor: '#fafafa' } }}
            >
              <TableCell
                sx={{
                  ...RESUMEN_COL.producto,
                  verticalAlign: 'top',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  lineHeight: 1.35,
                }}
              >
                {row.producto || '—'}
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.tipoArt, verticalAlign: 'top', overflow: 'visible' }}>
                {row.tipo ? (
                  <Chip
                    label={labelTipoArticulo(row.tipo)}
                    size="small"
                    sx={{
                      maxWidth: '100%',
                      bgcolor: '#fff9c4',
                      color: '#5d4037',
                      fontWeight: 500,
                      height: 'auto',
                      minHeight: 22,
                      alignSelf: 'flex-start',
                      '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        overflow: 'visible',
                        fontSize: '0.68rem',
                        px: '5px',
                        py: '4px',
                        display: 'block',
                        lineHeight: 1.35,
                      },
                    }}
                  />
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.ramo, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                <ChipClasificacion nivel="ramo" nombre={row.nombreRamo ?? ''} />
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.linea, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                <ChipClasificacion nivel="linea" nombre={row.nombreLinea ?? ''} />
              </TableCell>
              <TableCell sx={{ ...RESUMEN_COL.clase, py: 1, verticalAlign: 'top', overflow: 'visible' }}>
                <ChipClasificacion nivel="clase" nombre={row.nombreClase ?? ''} />
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.cantidad,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {qtyFormatter.format(row.cantidad)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.monto,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {currencyFormatter.format(row.montoTotal)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.pctMonto,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {pctFormatter.format(pctDelMonto(row.montoTotal, totalMonto))}%
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.costo,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {currencyFormatter.format(row.costoTotal ?? 0)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.pctRentab,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {fmtPctRentab(row.montoTotal, row.costoTotal ?? 0)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.peso,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {decimal3Formatter.format(row.pesoTotal)}
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  ...RESUMEN_COL.volumen,
                  verticalAlign: 'top',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {decimal3Formatter.format(row.volumenTotal)}
              </TableCell>
              <TableCell align="center" className="no-print" sx={{ ...RESUMEN_COL.accion, px: 0.25, verticalAlign: 'top' }}>
                <IconButton
                  size="small"
                  color="info"
                  aria-label="Ver detalle"
                  onClick={() => onAbrirDetalle(row)}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            sx={{
              bgcolor: '#e3f2fd',
              '& td': {
                fontWeight: 700,
                color: theme.palette.primary.main,
                borderTop: `2px solid ${theme.palette.primary.light}`,
              },
            }}
          >
            <TableCell colSpan={5} sx={{ fontSize: '0.8rem' }}>
              {filtroDrillDown ? 'TOTAL FILTRADO' : 'TOTAL GENERAL'}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.cantidad, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {qtyFormatter.format(totales.totalCantidad)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.monto, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {currencyFormatter.format(totales.totalMonto)}
            </TableCell>
            <TableCell align="right" sx={{ ...RESUMEN_COL.pctMonto, fontSize: '0.75rem' }}>
              100%
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.costo, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {currencyFormatter.format(totales.totalCosto)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.pctRentab, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {fmtPctRentab(totales.totalMonto, totales.totalCosto)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.peso, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {decimal3Formatter.format(totales.totalPeso)}
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...RESUMEN_COL.volumen, fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums' }}
            >
              {decimal3Formatter.format(totales.totalVolumen)}
            </TableCell>
            <TableCell className="no-print" sx={{ ...RESUMEN_COL.accion }} />
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
      )}
    </Box>
  );
};

export const ResumenClasificacionTabs: React.FC<ResumenClasificacionTabsProps> = ({
  rows,
  totalMonto,
  totales,
  onAbrirDetalle,
}) => {
  const [tab, setTab] = React.useState(0);
  const [filtroDrillDown, setFiltroDrillDown] = React.useState<FiltroDrillDown | null>(null);

  React.useEffect(() => {
    setFiltroDrillDown(null);
  }, [rows]);

  const handleDrillDown = React.useCallback((nivel: NivelChip, nombre: string) => {
    setFiltroDrillDown({ nivel, nombre });
    setTab(TAB_PRODUCTO);
  }, []);

  const rowsProducto = useMemo(() => {
    if (!filtroDrillDown) return rows;
    return filtrarRowsPorDrillDown(rows, filtroDrillDown);
  }, [rows, filtroDrillDown]);

  const totalesProducto = useMemo(() => {
    if (!filtroDrillDown) return totales;
    const t = sumRows(rowsProducto);
    return {
      totalCantidad: t.cantidad,
      totalMonto: t.montoTotal,
      totalCosto: t.costoTotal,
      totalPeso: t.pesoTotal,
      totalVolumen: t.volumenTotal,
    };
  }, [rowsProducto, filtroDrillDown, totales]);

  const montoBaseProducto = filtroDrillDown ? totalesProducto.totalMonto : totalMonto;

  const porRamo = useMemo(
    () => aggregateByField(rows, 'nombreRamo', totalMonto),
    [rows, totalMonto]
  );
  const porLinea = useMemo(
    () => aggregateByField(rows, 'nombreLinea', totalMonto),
    [rows, totalMonto]
  );
  const porClase = useMemo(
    () => aggregateByField(rows, 'nombreClase', totalMonto),
    [rows, totalMonto]
  );

  return (
    <Box sx={{ mb: 2 }} className="informe-resumen-clasificacion">
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Resumen por clasificación
      </Typography>
      <Tabs
        value={tab}
        onChange={(_, v) => {
          setTab(v);
          if (v !== TAB_PRODUCTO) setFiltroDrillDown(null);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5, textTransform: 'none' } }}
      >
        <Tab label="Ramo" />
        <Tab
          label={
            <Tooltip
              title="Total por nombre de línea en el período filtrado (puede repetirse en varios ramos)."
              arrow
            >
              <span>Línea</span>
            </Tooltip>
          }
        />
        <Tab label="Clase" />
        <Tab label="Producto" />
      </Tabs>

      {tab === TAB_RAMO && (
        <TablaAgregada label="Ramo" nivel="ramo" filas={porRamo} totales={totales} onDrillDown={handleDrillDown} />
      )}
      {tab === TAB_LINEA && (
        <TablaAgregada label="Línea" nivel="linea" filas={porLinea} totales={totales} onDrillDown={handleDrillDown} />
      )}
      {tab === TAB_CLASE && (
        <TablaAgregada label="Clase" nivel="clase" filas={porClase} totales={totales} onDrillDown={handleDrillDown} />
      )}
      {tab === TAB_PRODUCTO && (
        <TablaProductos
          rows={rowsProducto}
          totalMonto={montoBaseProducto}
          totales={totalesProducto}
          filtroDrillDown={filtroDrillDown}
          onLimpiarFiltro={() => setFiltroDrillDown(null)}
          onAbrirDetalle={onAbrirDetalle}
        />
      )}
    </Box>
  );
};
